import { BroadcastAction } from '@/shared/types/broadcast';

import { CarouselCard, QuestionSetConfig, TagOperation, WhatsAppFlowDisplay } from './action';
import { Attachment, Context, ConversationSource } from './chat';
import { ModelStrategyStep } from './modelStrategy';

export type Fragment = {
  score: number;
  fragment_id: string;
  document_id: string;
  datasource_id: string;
};

export type ToolInvocation = {
  mcp_id: string;
  name: string;
  arguments: Record<string, unknown>;
  result: Record<string, unknown>;
  succeeded: boolean;
  duration_ms?: number;
  meta: Record<string, unknown>;
};

export type ToolCallsPayload = { tool_invocations: ToolInvocation[] };

export type ExternalUrl = {
  external_url?: string;
  name: string;
  document_id?: string;
  datasource_id?: string;
};

/**
 * This tracks the reason why we called the Synthesis service.
 * Used for analytics and debugging purposes.
 */
export enum SynthesisRequestCode {
  UNSPECIFIED_REQUEST_CODE = 0,
  UNSUPPORTED_LANGUAGE = 1,
  NO_COLLECTION = 2,
  NO_INFERENCE_RESPONSE = 3,
  NO_MATCHING_RULE = 4,
  NO_DIALOG_RESPONSE = 5,
  DIALOG_UNKNOWN = 6,
  ERROR = 7,
}

/**
 * This tracks the result of the Synthesis response.
 * Used for analytics and debugging purposes.
 */
export enum SynthesisResponseCode {
  OK = 0,
  OK_FALLBACK = 1,
  NO_SOURCES = 2,
  NO_ANSWER = 3,
  NO_MESSAGE = 4,
  ERROR = 5,
  FORCE_HANDOVER = 6,
  NO_ANSWER_REPETITION = 7,
  NO_ANSWER_LINK_HALLUCINATION = 8,
  NO_ANSWER_JAILBREAK = 9,
  NO_ANSWER_FACT_HALLUCINATION = 10,
  NO_ANSWER_OUT_OF_SCOPE = 11,
  NO_ANSWER_LENGTH_EXCEEDED = 12,
  INTERRUPTED = 13,
  FORCE_WORKFLOW = 14,
  VOICEMAIL_DETECTED = 15,
  NO_ANSWER_TOOL_HALLUCINATION = 16,
  AUTHENTICATION_CONTINUE = 17,
}

export type TryItCollection = Partial<{
  collection_id: string;
  request_code: number;
  response_code?: number;
  model_id?: string;
  fragments?: Fragment[];
  external_urls?: ExternalUrl[];
}>;

/**
 * Explicit override applied to live-test sends. Same shape as a single
 * `ModelStrategyStep` — orchestrator collapses it into a one-step chain that
 * supersedes the configured strategy.
 */
export type LanguageModelOverride = ModelStrategyStep;

interface BaseResponseMessage {
  action_id: string;
}

interface TextResponseMessage extends BaseResponseMessage {
  type: 'text';
  text: string;
  options?: { label: string; text: string }[] | undefined;
}

interface UrlResponseMessage extends BaseResponseMessage {
  type: 'url';
  url: string;
}

interface AudioResponseMessage extends BaseResponseMessage {
  type: 'audio';
  url: string;
}

interface ImageResponseMessage extends BaseResponseMessage {
  type: 'image';
  url: string;
  name: string;
  id?: string;
}

interface VideoResponseMessage extends BaseResponseMessage {
  type: 'video';
  url: string;
  name: string;
  size: number;
  id?: string;
}

interface FileResponseMessage extends BaseResponseMessage {
  type: 'file';
  url: string;
  name: string;
  size: number;
  id?: string;
}

interface CarouselResponseMessage extends BaseResponseMessage {
  type: 'carousel';
  cards: CarouselCard[];
}

interface WebviewResponseMessage extends BaseResponseMessage {
  type: 'webview';
  url: string;
  name: string;
  label: string;
  height: string;
  trigger_node_id: string;
}

interface SurveyReponseMessage extends BaseResponseMessage {
  type: 'survey';
  url: string;
  name: string;
  label: string;
  height: string;
}
interface HandoverReponseMessage extends BaseResponseMessage {
  type: 'handover';
  name: string;
}

interface ReplayResponseMessage extends BaseResponseMessage {
  type: 'replay';
  text?: string;
}

interface WhatsAppFlowResponseMessage extends BaseResponseMessage {
  type: 'whatsapp_flow';
  trigger_node_id?: string;
  whatsapp_flow?: WhatsAppFlowDisplay;
}

interface QuestionSetResponseMessage extends BaseResponseMessage {
  type: 'question_set';
  name?: string;
  question_set?: QuestionSetConfig;
  trigger_node_id?: string;
}

interface SwitchChannelResponseMessage extends BaseResponseMessage {
  type: 'switch_channel';
  channel: string;
  integration_id?: string;
  url?: string;
  expires_at?: string;
  text?: string;
}

type Responses =
  | TextResponseMessage
  | UrlResponseMessage
  | AudioResponseMessage
  | ImageResponseMessage
  | VideoResponseMessage
  | FileResponseMessage
  | CarouselResponseMessage
  | WebviewResponseMessage
  | SurveyReponseMessage
  | HandoverReponseMessage
  | ReplayResponseMessage
  | WhatsAppFlowResponseMessage
  | QuestionSetResponseMessage
  | SwitchChannelResponseMessage;
export type ResponseMessage = Responses & { error_code?: string };

export type Entity = {
  confidence: number;
  start: number;
  end: number;
  entity: string;
  value: string;
  type: string;
};

export type NodeStack = {
  node_id: string;
  requisite_id?: string;
  request_id?: string;
  name: string;
  type: string;
};

export enum DebugStepType {
  REQUISITE = 0,
  ACTION = 1,
  CONDITION = 2,
  PREFILL = 3,
  NODE = 4,
  INFINITE_LOOP = 6,
  SLOT = 7,
  WEBHOOK = 8,
  MCP_TOOL = 9,
  AUTH = 10,
  AI_RESPONSE = 11,
}

export enum AuthEventType {
  UNSPECIFIED_AUTH_EVENT = 0,
  AUTH_STEP_ENTERED = 1,
  AUTH_PRE_ENTER_WEBHOOK = 2,
  AUTH_VALIDATE_INPUT = 3,
  AUTH_EXIT = 4,
  AUTH_VALIDATOR_SETUP_ERROR = 6,
}

export enum AuthValidationResult {
  UNSPECIFIED_VALIDATION_RESULT = 0,
  VALID = 1,
  INVALID = 2,
  ERROR = 3,
}

export enum AuthTerminalCause {
  UNSPECIFIED_TERMINAL_CAUSE = 0,
  THRESHOLD_MET = 1,
  MAX_ATTEMPTS = 2,
  TIMEOUT = 3,
  SYSTEM_FAILURE = 4,
  LLM_EXIT = 5,
}

// Numeric values mirror the proto enum tags. Member names are short because
// TypeScript scopes them under the enum type — proto-level prefixes
// (AUTH_STEP_TYPE_*) only exist on the wire to avoid file-namespace clashes.
export enum AuthStepType {
  UNSPECIFIED = 0,
  IDENTIFICATION = 1,
  VERIFICATION = 2,
}

export enum AuthValidationMethod {
  UNSPECIFIED = 0,
  EXACT_MATCH = 1,
  FUZZY_MATCH = 2,
  GUIDELINE = 3,
  WEBHOOK = 4,
  FORMAT = 5,
  CONTAINS = 6,
  STARTS_WITH = 7,
  ENDS_WITH = 8,
}

export enum AuthStepStatus {
  UNSPECIFIED = 0,
  PENDING = 1,
  IN_PROGRESS = 2,
  COMPLETED = 3,
}

export enum AuthAgentResultCode {
  UNSPECIFIED = 0,
  CONTINUE = 1,
  SUCCESS = 2,
  FAILURE = 3,
  SYSTEM_FAILURE = 4,
  SKIPPED = 5,
}

export type AuthDebugStep = {
  event: AuthEventType;
  step?: AuthStepType;
  question_key?: string;
  input_value?: string;
  validation_result?: AuthValidationResult;
  validation_method?: AuthValidationMethod;
  terminal_cause?: AuthTerminalCause;
  ground_truth?: string;
  fuzzy_score?: number;
  /** contains / starts_with / ends_with: configured match size. */
  match_size?: number;
  missing_context_variable?: string;
  /** validator_setup_error: ground truth length when match_size exceeds it. */
  ground_truth_length?: number;
};

// Every field is optional — proto3 strips zero/empty values across the wire,
// so a snapshot fresh from the dialog backend may legitimately be missing
// `score: 0`, `validated_question_keys: []`, etc. Consumers must apply defaults.
export type AuthenticationDebug = {
  current_step?: AuthStepType;
  status?: AuthStepStatus;
  score?: number;
  failed_attempts?: number;
  system_errors?: number;
  validated_question_keys?: string[];
  final_result_code?: AuthAgentResultCode;
};

export enum DebugStepSubType {
  UNSPECIFIED_SUB_TYPE = 0,
  RULE = 1,
  LLM = 2,
  FIRST_MESSAGE_WEBHOOK = 3,
  PRE_MESSAGE_WEBHOOK = 4,
  POST_MESSAGE_WEBHOOK = 5,
  WORKFLOW_TRIGGERED = 6,
  MCP_TOOL_CALL = 7,
  STARTING_NODE = 8,
}

export enum ContextChangeType {
  UPDATED = 0,
  ADDED = 1,
  REMOVED = -1,
}

export type WebhookError = {
  code?: string;
  message?: string;
  details?: string;
  status_code?: number;
};

export type DebugStep = {
  type: DebugStepType;
  action_id?: string;
  requisite_id?: string;
  condition_id?: string;
  webhook_id?: string;
  node_id?: string;
  context_changes?: { [key: string]: ContextChangeType };
  duration?: number;
  intent_match_theshold?: number;
  intent_match_confidence?: number;
  tags?: string[];
  action_type?: string;
  sub_steps?: DebugStep[];
  webhook_error?: WebhookError;
  tag_operation?: TagOperation;
  sub_type?: DebugStepSubType;
  reminder_id?: string;
  reminder_seconds?: number;
  mcp_server_id?: string;
  mcp_tool_name?: string;
  auth?: AuthDebugStep;
};

type DialogDebug = {
  nodes_stack: NodeStack[];
  logs: string[];
  steps: DebugStep[];
  collection: TryItCollection;
  standalone_question?: string;
  tools_invoked?: ToolInvocation[];
  request_code?: number;
  response_code?: number;
  // Only emitted while an auth sub-agent is in flight; cleared on terminal exit.
  authentication?: AuthenticationDebug;
};

type CorrectionTask = 'standalone' | 'synthesis' | 'planner' | 'enhance_unknown';

export type AmendPayload = {
  request_id: string;
  task: CorrectionTask;
  session_id: string;
  language: string;
  simulation?: { brain_id: string; run_id: string; evaluation_id: string };
};

export interface Correction {
  task: CorrectionTask;
  time: string;
  language: string;
  edited_by: string;
  correction: string;
}
type Output = {
  intents: { intent: string; confidence: number }[];
  entities: Entity[];
  debug: DialogDebug;
  responses: ResponseMessage[];
  planner_intents: string[];
  corrections?: Correction[];
};
export type Response = {
  input: { text: string };
  session_id: string;
  context: Context;
  output: Output;
};

type BaseMessage = { id?: string };

export type MessageReminder = {
  reminder_id: string;
  trigger_node_id: string;
  /** @deprecated Use reminder_seconds instead. Kept for backward compatibility only. */
  minutes?: number;
  reminder_seconds?: number;
};

export type UserMessage = BaseMessage & {
  type: 'user';
  intents: { confidence: number; intent: string }[] | null | undefined;
  entities: Entity[] | undefined;
  text: string;
  isAudio?: boolean;
  attachments?: Attachment[];
  request_id?: string;
  reminder?: MessageReminder;
  isFlowResponse?: boolean;
};

export type BrainMessage = BaseMessage & { type: 'brain'; responses: ResponseMessage[] };

export type AgentMessage = BaseMessage & {
  type: 'agent';
  text: string;
  request_id: string;
  template?: BroadcastAction;
  body?: Record<string, string>;
  attachments?: Attachment[];
};

export type ErrorMessage = BaseMessage & { type: 'error'; text: string };

export type CollectionMessage = BaseMessage & { type: 'collection'; text: string };

export type BroadcastMessage = BaseMessage & { type: 'broadcast'; responses: ResponseMessage[] };
export type CampaignMessage = BaseMessage & { type: 'campaign'; responses: ResponseMessage[] };

export type Message =
  | UserMessage
  | BrainMessage
  | AgentMessage
  | ErrorMessage
  | CollectionMessage
  | BroadcastMessage
  | CampaignMessage;

export type NodeType = {
  channel?: string;
  integration_id?: string;
  nodes_stack?: NodeStack[];
  key?: string | null;
  messages: Message[];
  context: Context | undefined;
  debugLogs?: string[] | undefined;
  isComplete?: boolean;
  id?: string | null;
  name?: string | null;
  author_type?: 'user' | 'agent' | 'brain' | 'tool';
  author_id?: string;
  time?: string;
  steps?: DebugStep[];
  collection?: TryItCollection;
  request_id?: string;
  intents?: { intent: string; confidence: number }[];
  planner_intents?: string[];
  standalone_question?: string;
  corrections?: Correction[];
  tags?: string[];
  broadcast_id?: string;
  broadcast_name?: string;
  campaign_id?: string;
  campaign_name?: string;
  reaction?: ReactionType;
  tool_calls?: ToolCallsPayload;
  authentication?: AuthenticationDebug;
};

export type MessageSent = {
  session_id: string;
  channel: string;
  input: { text: string };
  context: Context;
  collection_id?: string;
  match_draft?: boolean;
  /** One-off model + reasoning override (test modal). Orchestrator treats these as a single explicit step that takes precedence over the configured strategy. */
  metadata?: {
    language_model_id?: string;
    language_model_alias?: string;
    reasoning_effort?: 'low' | 'medium' | 'high';
  };
};

type BaseReaction = '1' | '-1';
export type ReactionType = BaseReaction | null;
